import {
  TextField,
  useForm,
  PrimaryButton,
  SecondaryButton,
  StarIcon,
  XIcon,
  HelpIcon,
  Text,
  PortingNumbersIcon,
  SkeletonLoader,
} from '@weave/design-system';
import {
  EntityTagState,
  TagExistReason,
} from '@weave/schema-gen-ts/dist/apis/api/wetags/v1/api.pb';
import { reportEntityTagState } from 'apis/wetags';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { WetagsTable, cellStyle, rowStyle } from '../components/WetagsTable.component';
import { FormattedDate } from '../components/FormattedDate.component';
import { theme } from '@weave/theme';
import { EntityTagHistoryModal } from './EntityTagHistoryModal.component';

export function CheckEntity({
  namespaceId,
  tagId,
  onDeleteEntity,
  currentPercentage,
  isDeletingEntity = false,
  showTagHistoryVisualization = true,
}: {
  namespaceId: string;
  tagId: string;
  currentPercentage?: number;
  onDeleteEntity?: (entityId: string, onDeleted?: () => void | Promise<void>) => void;
  isDeletingEntity?: boolean;
  showTagHistoryVisualization?: boolean; //When false, only renders the Table
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<EntityTagState | null>(null);
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [lastCheckedEntityId, setLastCheckedEntityId] = useState<string | null>(null);
  const [historyTarget, setHistoryTarget] = useState<{
    namespaceId: string;
    tagId: string;
    entityId: string;
  } | null>(null);
  const prevPercentageRef = useRef(currentPercentage);

  useEffect(() => {
    const prevPercentage = prevPercentageRef.current;
    prevPercentageRef.current = currentPercentage;

    if (
      prevPercentage !== undefined &&
      currentPercentage !== prevPercentage &&
      lastCheckedEntityId
    ) {
      onSubmit(lastCheckedEntityId, true);
    }
  }, [currentPercentage]);

  const onSubmit = useCallback(
    async (entityValue: string, isReValidate = false) => {
      isReValidate ? setIsRevalidating(true) : setIsLoading(true);
      try {
        const response = await reportEntityTagState({
          namespaceId,
          tagId,
          entityId: entityValue || '',
        });
        setResults(response.state || null);
        setLastCheckedEntityId(entityValue);
      } catch (error) {
        console.error('Error checking entity tag state:', error);
        if (!isReValidate) setResults(null);
      } finally {
        isReValidate ? setIsRevalidating(false) : setIsLoading(false);
      }
    },
    [namespaceId, tagId]
  );

  const { formProps, values, getFieldProps } = useForm({
    fields: {
      entityValue: {
        type: 'text',
        required: true,
      },
    },
    onSubmit: async (values) => {
      values.entityValue && onSubmit(values.entityValue);
    },
  });

  const historyInputId = values.entityValue?.trim() ?? '';

  return (
    <div>
      <form
        {...formProps}
        css={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto auto',
          gap: theme.spacing(1),
          alignItems: 'start',
        }}
      >
        <TextField
          label="Entity ID"
          disabled={isLoading}
          placeholder="Entity id (e.g., 9dfc604e-8539-4415-94d5-94e134855959)"
          {...getFieldProps('entityValue')}
        />

        <PrimaryButton
          type="submit"
          disabled={isLoading}
          style={{ width: 'max-content' }}
        >
          Check
        </PrimaryButton>

        <PrimaryButton
          type="button"
          disabled={
            isLoading || !!historyTarget || !namespaceId || !tagId || !historyInputId
          }
          style={{ width: 'max-content' }}
          onClick={() =>
            setHistoryTarget({ namespaceId, tagId, entityId: historyInputId })
          }
        >
          Get Tag History
        </PrimaryButton>

        <SecondaryButton
          type="button"
          disabled={isLoading || results == null}
          style={{ width: 'max-content' }}
          onClick={() => {
            setResults(null);
            setLastCheckedEntityId(null);
          }}
        >
          Clear
        </SecondaryButton>
      </form>
      {historyTarget && (
        <EntityTagHistoryModal
          {...historyTarget}
          onClose={() => setHistoryTarget(null)}
          showVisualization={showTagHistoryVisualization}
        />
      )}
      {!isLoading && results != null ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1),
            background: theme.colors.neutral10,
            padding: theme.spacing(2),
            borderRadius: theme.borderRadius.medium,
            marginTop: theme.spacing(2),
            opacity: isRevalidating ? 0.5 : 1,
            transition: 'opacity 0.3s ease',
          }}
        >
          {isRevalidating && (
            <>
              <Text size="small" style={{ color: theme.colors.neutral50 }}>
                Rechecking - rollout percentage changed…
              </Text>
            </>
          )}
          <WetagsTable style={{ maxWidth: 600 }}>
            <tbody>
              <tr style={rowStyle}>
                <td style={{ ...cellStyle, fontWeight: 600, width: '40%' }}>Entity ID</td>
                <td style={{ ...cellStyle, fontFamily: 'monospace' }}>
                  {results?.entityId}
                </td>
              </tr>
              <tr style={rowStyle}>
                <td style={{ ...cellStyle, fontWeight: 600 }}>Enabled</td>
                <td style={cellStyle}>{results?.exists ? '✅ True' : '❌ False'}</td>
              </tr>
              {results?.reason && (
                <tr style={rowStyle}>
                  <td style={{ ...cellStyle, fontWeight: 600 }}>Reason</td>
                  <td style={cellStyle}>
                    <TagReasonComponent reason={results?.reason} />
                  </td>
                </tr>
              )}
              {results?.createTime && (
                <tr style={rowStyle}>
                  <td style={{ ...cellStyle, fontWeight: 600 }}>Created Time</td>
                  <td style={cellStyle}>
                    <FormattedDate value={results?.createTime} />
                  </td>
                </tr>
              )}
              {results?.startingPercentage && (
                <tr style={rowStyle}>
                  <td style={{ ...cellStyle, fontWeight: 600 }}>Starting Percentage</td>
                  <td style={cellStyle}>{results?.startingPercentage}%</td>
                </tr>
              )}
            </tbody>
          </WetagsTable>
          {results?.exists && (
            <PrimaryButton
              type="button"
              disabled={
                results.reason !== TagExistReason.ENTITY_EXPLICITLY_TAGGED ||
                isDeletingEntity
              }
              destructive
              onClick={() => {
                if (results?.entityId) {
                  onDeleteEntity?.(results.entityId, () =>
                    onSubmit(results.entityId || '')
                  );
                }
              }}
              style={{ width: 'max-content' }}
            >
              Delete Entity
            </PrimaryButton>
          )}
        </div>
      ) : (
        <>
          {isLoading && (
            <div
              style={{
                gap: theme.spacing(1),
                paddingBottom: theme.spacing(2),
                marginTop: theme.spacing(2),
              }}
            >
              <SkeletonLoader
                animation="shimmer"
                width={'100%'}
                height={20}
                count={6}
                distance={10}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function formatTagReason(reason: TagExistReason): {
  label: React.ReactNode;
  text: string;
} {
  switch (reason) {
    case TagExistReason.ENTITY_EXPLICITLY_TAGGED:
      return {
        label: (
          <ReasonLabel
            icon={<StarIcon size={16} />}
            text="Explicitly Tagged"
            background={theme.colors.secondary.seaweed40}
            color={theme.colors.white}
          />
        ),
        text: 'Entity is tagged independently of percentage rollout.',
      };
    case TagExistReason.ENTITY_IN_TAG_PERCENT:
      return {
        label: (
          <ReasonLabel
            icon={<PortingNumbersIcon size={16} />}
            text="Percentage"
            background={theme.colors.primary40}
            color={theme.colors.white}
          />
        ),
        text: 'Entity is tagged as part of the percentage rollout.',
      };
    case TagExistReason.ENTITY_NOT_TAGGED:
      return {
        label: (
          <ReasonLabel
            icon={<XIcon size={16} />}
            text="Not Tagged"
            background={theme.colors.critical40}
            color={theme.colors.white}
          />
        ),
        text: 'Entity is not tagged.',
      };
    case TagExistReason.TAG_EXIST_REASON_UNSPECIFIED:
      return {
        label: (
          <ReasonLabel
            icon={<HelpIcon size={16} />}
            text="Unspecified"
            background={theme.colors.neutral30}
            color={theme.colors.white}
          />
        ),
        text: 'Tag reason is unspecified.',
      };
    default:
      return {
        label: (
          <ReasonLabel
            icon={<HelpIcon size={16} />}
            text="Unknown"
            background={theme.colors.neutral30}
            color={theme.colors.white}
          />
        ),
        text: 'Unknown reason.',
      };
  }
}

const ReasonLabel = ({
  icon,
  text,
  color,
  background,
}: {
  icon: React.ReactNode;
  text: string;
  color?: string;
  background?: string;
}) => {
  return (
    <span
      style={{
        borderRadius: theme.borderRadius.small,
        padding: theme.spacing(0.5, 1),
        background: background || color || theme.colors.neutral20,
        width: 'fit-content',
        color: color || theme.colors.black,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        fontWeight: 600,
      }}
    >
      {icon} {text}
    </span>
  );
};

const TagReasonComponent = ({ reason }: { reason: TagExistReason }) => {
  const { label, text } = formatTagReason(reason);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
      {label}
      <Text>{text}</Text>
    </div>
  );
};
